import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationItem,
  NotificationListResponse,
  NotificationProvidersStatus,
} from '@crm/shared';
import { api } from '../../lib/api';

const BASE = '/notifications';

export const notificationKeys = {
  inApp: (unreadOnly: boolean) => ['notifications', 'in-app', { unreadOnly }] as const,
  providers: ['notifications', 'providers'] as const,
};

export function useInAppNotifications(unreadOnly = false, refetchInterval = 60_000) {
  return useQuery({
    queryKey: notificationKeys.inApp(unreadOnly),
    queryFn: async (): Promise<NotificationListResponse> => {
      const res = await api.get<NotificationListResponse>(`${BASE}/in-app`, {
        params: { unreadOnly: unreadOnly ? 'true' : 'false', limit: 50 },
      });
      return res.data;
    },
    refetchInterval,
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<NotificationItem> => {
      const res = await api.post<NotificationItem>(`${BASE}/in-app/${id}/read`);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'in-app'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ updated: number }> => {
      const res = await api.post<{ updated: number }>(`${BASE}/in-app/read-all`);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'in-app'] });
    },
  });
}

export function useNotificationProviders() {
  return useQuery({
    queryKey: notificationKeys.providers,
    queryFn: async (): Promise<NotificationProvidersStatus> => {
      const res = await api.get<NotificationProvidersStatus>(`${BASE}/providers`);
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}
